// src/features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/api.ts
//
// Typed client for the Multi-Asset Portfolio Optimizer backend
// (FastAPI router mounted at /api/v1/multi-asset-portfolio). It talks to the
// shared `apiClient` axios instance, which attaches the WordPress JWT and
// already bakes the `/api/v1` version prefix into its baseURL — so we request
// the bare `/multi-asset-portfolio/*` paths here (adding `/api/v1` would
// double the prefix and 404).
//
// Endpoint map (see backend/app/api/multi_asset_portfolio.py):
//   GET  /meta/bl-defaults      -> optimiser "rules of the game" (form defaults)
//   GET  /meta/active-sleeves   -> canonical sleeves + strategic prior weights
//   GET  /tier3/risk-aversion   -> dynamic Black-Litterman delta for a regime
//   POST /builder/global        -> full 3-tier nested optimisation
//   POST /publish/target-weights-> persist approved weights to TimescaleDB
//   POST /publish/ml-insights   -> persist the Black-Litterman Quant X-Ray

import axios from 'axios';

import { apiClient } from '../../../api/client';

// --- Meta: BL defaults ----------------------------------------------------

export interface BLConfig {
    /** Default market risk aversion (delta) for the reverse optimisation. */
    risk_aversion: number;
    /** Scalar for the prior covariance matrix (uncertainty in Pi). */
    tau: number;
    /** Maximum concentration cap for a single sleeve. */
    w_max: number;
    /** Whether shorting is disallowed (weights floored at 0). */
    long_only: boolean;
}

export interface BLDefaultsResponse {
    bl_config: BLConfig;
    /** Base risk aversion before the Tier-3 macro-regime scaling is applied. */
    base_delta: number;
    /** Global aggressiveness dial for how hard ML signals tilt the views. */
    tilt_scalar: number;
}

export const fetchBlDefaults = async (): Promise<BLDefaultsResponse> => {
    const { data } = await apiClient.get<BLDefaultsResponse>(
        '/multi-asset-portfolio/meta/bl-defaults',
    );
    return data;
};

// --- Meta: active sleeves -------------------------------------------------

export interface ActiveSleevesResponse {
    /** Canonical list of active asset-class sleeves, in strict order. */
    sleeves: string[];
    /** Strategic baseline weight for each sleeve (risky sleeves sum to 1.0). */
    prior_weights: Record<string, number>;
    /** Human-readable explanation of how cash is handled in the optimiser. */
    budget_note: string;
}

export const fetchActiveSleeves = async (): Promise<ActiveSleevesResponse> => {
    const { data } = await apiClient.get<ActiveSleevesResponse>(
        '/multi-asset-portfolio/meta/active-sleeves',
    );
    return data;
};

// --- Tier 3: dynamic risk aversion ----------------------------------------

export interface RiskAversionResponse {
    /** The final scaled delta used in the optimiser. */
    risk_aversion: number;
    inputs: {
        regime_label: string;
        is_transition_imminent: boolean;
        base_delta: number;
    };
}

export const fetchDynamicRiskAversion = async (
    regimeLabel: string,
    isTransitionImminent: boolean,
    baseDelta: number,
): Promise<RiskAversionResponse> => {
    const { data } = await apiClient.get<RiskAversionResponse>(
        '/multi-asset-portfolio/tier3/risk-aversion',
        {
            params: {
                regime_label: regimeLabel,
                is_transition_imminent: isTransitionImminent,
                base_delta: baseDelta,
            },
        },
    );
    return data;
};

// --- Tier 1-3: the global orchestrator ------------------------------------

export interface PricingRow {
    date: string;
    symbol: string;
    adj_close: number;
}

export interface SignalRow {
    symbol: string;
    forge_ultra_signal: number;
    master_probability?: number | null;
    side?: number;
}

export interface GlobalBuilderRequest {
    /** Map of sleeve_id -> long-format pricing panel. */
    sleeve_pricing: Record<string, { rows: PricingRow[] }>;
    /** Map of sleeve_id -> per-symbol ML signals. */
    sleeve_signals: Record<string, { rows: SignalRow[] }>;
    macro_regime_label: string;
    is_transition_imminent: boolean;
    total_risky_budget: number;
    base_delta: number;
    tilt_scalar: number;
}

export interface GlobalPortfolioRow {
    symbol: string;
    asset_class: string;
    /** Final portfolio weight (Tier-1 * Tier-2), a fraction in [0, 1]. */
    target_weight: number;
    /** Intra-sleeve micro weight. */
    tier1_weight: number;
    /** Cross-asset macro weight for the sleeve. */
    tier2_weight: number;
}

export interface GlobalBuilderResponse {
    /** Flat allocation array, sorted by descending target_weight. */
    portfolio: GlobalPortfolioRow[];
    /** Backtest + simulation summary (populated by the live orchestrator). */
    metrics?: SimulationMetrics | null;
    /** Walk-forward historical backtest equity curve. */
    equity_curve?: EquityCurvePoint[] | null;
}

export const buildGlobalPortfolio = async (
    payload: GlobalBuilderRequest,
): Promise<GlobalBuilderResponse> => {
    const { data } = await apiClient.post<GlobalBuilderResponse>(
        '/multi-asset-portfolio/builder/global',
        payload,
    );
    return data;
};

// --- Live DB-hydrated orchestrator (thin-UI / fat-backend) ----------------
//
// The interactive cockpit uses THIS endpoint, not `buildGlobalPortfolio`
// above. The browser ships only the user's dials; the backend hydrates the
// heavy per-sleeve pricing + signals from TimescaleDB internally (reusing the
// production `GlobalPortfolioService`), runs the 3-tier optimiser natively and
// returns the final 20-30 real weights. No mock, no giant client payload.

export interface UIBuilderRequest {
    /** Macro regime override; omit to use the DB's latest regime. */
    macro_regime_label?: string | null;
    /** Institutional SAA risk band (e.g. 'Balanced'); drives sleeve bounds. */
    risk_profile: string;
    is_transition_imminent: boolean;
    /** Risky capital budget in [0, 1]; remainder is implicit cash. */
    total_risky_budget: number;
    base_delta: number;
    tilt_scalar: number;
    /** Path-dependent Numba backtest dials (capital, cash flows, drag). */
    simulation: SimulationConfig;
}

export const buildGlobalPortfolioLive = async (
    payload: UIBuilderRequest,
): Promise<GlobalBuilderResponse> => {
    const { data } = await apiClient.post<GlobalBuilderResponse>(
        '/multi-asset-portfolio/builder/global/live',
        payload,
    );
    return data;
};

// --- Step 5: persistence --------------------------------------------------

export interface PublishResponse {
    upserted: number;
}

export interface PublishTargetWeightsRequest {
    /** ISO date string (YYYY-MM-DD). */
    date: string;
    domain: string;
    risk_profile: string;
    rows: { symbol: string; weight: number }[];
}

export const publishTargetWeights = async (
    payload: PublishTargetWeightsRequest,
): Promise<PublishResponse> => {
    const { data } = await apiClient.post<PublishResponse>(
        '/multi-asset-portfolio/publish/target-weights',
        payload,
    );
    return data;
};

export interface InsightPublishRow {
    symbol: string;
    ml_side: number;
    master_probability: number;
    expected_return_q: number;
    uncertainty_omega: number;
    phase_1_z_score?: number | null;
}

export interface PublishInsightsRequest {
    date: string;
    domain: string;
    rows: InsightPublishRow[];
}

export const publishMlInsights = async (
    payload: PublishInsightsRequest,
): Promise<PublishResponse> => {
    const { data } = await apiClient.post<PublishResponse>(
        '/multi-asset-portfolio/publish/ml-insights',
        payload,
    );
    return data;
};

// ============================================================================
// SIMULATION, BACKTESTING & SAVED REPORTS
// ============================================================================
//
// The live orchestrator (/builder/global/live) now runs a path-dependent Numba
// backtest driven by the `simulation` dials on UIBuilderRequest and returns the
// true historical Net Portfolio Value curve + summary metrics. A generated book
// can then be persisted as a named "Report" (the Saved Portfolios router mounted
// at /api/v1/reports -> bare `/reports/` through the versioned apiClient).

/** How often the user injects additional capital. */
export type CashFlowFrequency =
    | 'none'
    | 'monthly'
    | 'quarterly'
    | 'semiannually'
    | 'annually';

/** How often the portfolio is realigned to the target weights. */
export type RebalanceFrequency =
    | 'drift'
    | 'monthly'
    | 'quarterly'
    | 'semiannually'
    | 'annually';

/** Path-dependent dials for the historical backtest / forward simulation. */
export interface SimulationConfig {
    /** Starting capital in base currency (>= 100). */
    initial_investment: number;
    /** Amount injected at each cash-flow interval. */
    additional_investment: number;
    /** Cadence of the additional capital injections. */
    cash_flow_frequency: CashFlowFrequency;
    /** How frequently to rebalance back to the target weights. */
    rebalance_frequency: RebalanceFrequency;
    /** Tax rate applied to realized capital gains (e.g. 15.0 for 15%). */
    tax_rate_percent: number;
    /** Brokerage commissions in basis points applied to turnover. */
    trading_fee_bps: number;
}

/** Summary statistics for the generated + backtested portfolio. */
export interface SimulationMetrics {
    // Gross (math-only: no fees, no taxes).
    gross_expected_return_ann: number;
    gross_volatility: number;
    gross_sharpe: number;
    // Net (after simulated drift, fees, and taxes).
    net_expected_return_ann: number;
    net_volatility: number;
    net_sharpe: number;
    // Drag totals & drawdown.
    total_taxes_paid: number;
    total_fees_paid: number;
    final_net_value: number;
    max_drawdown: number;
}

/** A single daily point on the historical backtest chart. */
export interface EquityCurvePoint {
    /** ISO date string. */
    date: string;
    /** Total net liquidating value on this day. */
    portfolio_value: number;
    /** Initial capital + cumulative cash flows injected to date. */
    total_invested: number;
    /** Cash injected on this specific day. */
    cash_flows_injected: number;
    /** Taxes paid on this specific day. */
    tax_drag: number;
    /** Fees paid on this specific day. */
    fee_drag: number;
}

/** Payload persisted when the user saves a strategy to "Reports". */
export interface SaveReportRequest {
    report_name: string;
    macro_regime_label: string;
    risk_profile: string;
    is_transition_imminent: boolean;
    total_risky_budget: number;
    base_delta: number;
    tilt_scalar: number;
    simulation: SimulationConfig;
    portfolio: GlobalPortfolioRow[];
    metrics: SimulationMetrics;
    equity_curve: EquityCurvePoint[];
}

export interface SaveReportResponse {
    report_id: string;
    message: string;
}

/**
 * Persist a generated portfolio configuration, holdings snapshot and backtest.
 *
 * The Saved Portfolios (Reports) FastAPI router is mounted at
 * `/api/v1/reports`; the shared `apiClient` already bakes in the `/api/v1`
 * prefix, so we POST to the bare `/reports/` path here.
 */
export const savePortfolioReport = async (
    payload: SaveReportRequest,
): Promise<SaveReportResponse> => {
    const { data } = await apiClient.post<SaveReportResponse>(
        '/reports/',
        payload,
    );
    return data;
};

// ============================================================================
// SAVED STRATEGIES DASHBOARD (LIST, UPDATE, DELETE)
// ============================================================================
//
// Powers the "My Strategies" master hub. The Saved Portfolios (Reports) router
// is mounted at /api/v1/reports; the shared `apiClient` already bakes in the
// `/api/v1` prefix, so we hit the bare `/reports/*` paths here. These endpoints
// mirror backend/app/api/saved_portfolios.py:
//   GET    /reports/         -> paginated, searchable header/KPI list
//   PATCH  /reports/{id}     -> rename or toggle nightly live-tracking
//   DELETE /reports/{id}     -> permanently delete (cascades track record)

/** Header/KPI row for the dashboard list view (no holdings / no curve). */
export interface SavedReportListItem {
    id: string;
    name: string;
    /** ISO timestamp string. */
    created_at: string;
    macro_regime_label: string;
    risk_profile: string;
    net_expected_return_ann: number;
    net_sharpe: number;
    max_drawdown: number;
    total_fees_paid: number;
    total_taxes_paid: number;
    is_active_for_live_tracking: boolean;
    /** True when the Robo-Advisor autonomously manages this strategy. */
    is_auto_managed?: boolean;
}

/** Paginated list envelope returned by GET /reports/. */
export interface SavedReportListResponse {
    total: number;
    items: SavedReportListItem[];
}

/** Whitelisted server-side sort columns for the strategies list. */
export type SavedReportSortField =
    | 'created_at'
    | 'name'
    | 'net_sharpe'
    | 'max_drawdown';

/**
 * Fetch a paginated, searchable, header-only page of saved strategies.
 *
 * The backend caps `limit` at 100 and whitelists the `sort` column, so any
 * unexpected value silently falls back to `created_at desc`.
 */
export const fetchSavedStrategies = async (
    limit = 50,
    offset = 0,
    sort: SavedReportSortField = 'created_at',
    order: 'asc' | 'desc' = 'desc',
    query = '',
): Promise<SavedReportListResponse> => {
    const { data } = await apiClient.get<SavedReportListResponse>('/reports/', {
        params: { limit, offset, sort, order, q: query || undefined },
    });
    return data;
};

/** Permanently delete a saved strategy (cascades holdings + track record). */
export const deleteSavedStrategy = async (id: string): Promise<void> => {
    await apiClient.delete(`/reports/${id}`);
};

/** Toggle the nightly walk-forward live-tracking daemon for a strategy. */
export const updateStrategyTracking = async (
    id: string,
    isActive: boolean,
): Promise<void> => {
    await apiClient.patch(`/reports/${id}`, {
        is_active_for_live_tracking: isActive,
    });
};

// ============================================================================
// TEAR SHEET & BENCHMARK COMPARISON
// ============================================================================
//
// Powers the Strategy Tear Sheet (/dashboard/my-strategies/:reportId). Three
// backend endpoints back this view (see backend/app/api/saved_portfolios.py and
// backend/app/api/benchmarks.py; the shared apiClient bakes in /api/v1):
//   GET /reports/{id}                      -> full report header + holdings + curve
//   GET /benchmarks/catalogue              -> blended-ETF baselines for the dropdown
//   GET /benchmarks/compare/{id}?...       -> date-aligned curves + relative alpha
//   GET /reports/{id}/download-csv         -> zero-disk-I/O holdings CSV export

/** A single equity-curve point that also carries the out-of-sample flag. */
export type TrackRecordPoint = EquityCurvePoint & { is_out_of_sample: boolean };

/** Full saved-report payload for the detail dashboard (header + holdings + curve). */
export interface SavedReportDetailResponse {
    id: string;
    name: string;
    created_at: string;
    macro_regime_label: string;
    risk_profile: string;
    /** Not returned by the detail endpoint today — kept optional for forward-compat. */
    is_active_for_live_tracking?: boolean;
    net_expected_return_ann: number;
    net_volatility: number;
    net_sharpe: number;
    max_drawdown: number;
    total_taxes_paid: number;
    total_fees_paid: number;
    /** True when the Robo-Advisor autonomously manages this strategy. */
    is_auto_managed?: boolean;
    portfolio: GlobalPortfolioRow[];
    equity_curve: TrackRecordPoint[];
}

/** A single benchmark available in the comparison catalogue. */
export interface BenchmarkItem {
    id: string;
    name: string;
    type: 'ticker' | 'blend';
    description: string;
    /** Risk bands this benchmark suits; empty => suitable for all profiles. */
    risk_profiles: string[];
}

/** One calendar month of active (excess) return over the benchmark. */
export interface MonthlyExcessAlphaPoint {
    month: string;
    port_return: number;
    bench_return: number;
    excess: number;
    /**
     * True when the Robo-Advisor floored a negative excess to 0.0 for display
     * (the "Always Positive" product guarantee). Absent/false for the raw
     * benchmark-compare endpoint and for genuinely positive months.
     */
    is_defended?: boolean;
}

/** The benchmark auto-suggested for a given risk band / volatility. */
export interface BenchmarkSuggestionResponse {
    suggested_id: string;
    suggested_name: string;
    reason: string;
}

export interface BenchmarkListResponse {
    items: BenchmarkItem[];
}

/** Institutional active-management (alpha) metrics vs. the chosen benchmark. */
export interface RelativeMetrics {
    active_return_ann: number;
    tracking_error_ann: number;
    information_ratio: number;
    max_drawdown_diff: number;
    hit_rate_percent: number;
}

/** Headline absolute KPIs (mirrors the backend SavedReportMetricsResponse). */
export interface SavedReportMetricsResponse {
    net_expected_return_ann: number;
    net_volatility: number;
    net_sharpe: number;
    max_drawdown: number;
    total_taxes_paid: number;
    total_fees_paid: number;
}

/** Master payload for the portfolio-vs-benchmark outperformance view. */
export interface BenchmarkCompareResponse {
    portfolio_metrics: SavedReportMetricsResponse;
    benchmark_metrics: SavedReportMetricsResponse;
    relative: RelativeMetrics;
    portfolio_curve: TrackRecordPoint[];
    benchmark_curve: TrackRecordPoint[];
    /** Per-month active-return attribution (excludes the in-progress month). */
    monthly_excess_alpha: MonthlyExcessAlphaPoint[];
}

/** Fetch the full saved report (header + holdings + equity curve). */
export const fetchSavedReportDetail = async (
    id: string,
): Promise<SavedReportDetailResponse> => {
    const { data } = await apiClient.get<SavedReportDetailResponse>(
        `/reports/${id}`,
    );
    return data;
};

/** Fetch the catalogue of blended-ETF benchmarks for the comparison dropdown. */
export const fetchBenchmarkCatalogue = async (): Promise<BenchmarkListResponse> => {
    const { data } = await apiClient.get<BenchmarkListResponse>(
        '/benchmarks/catalogue',
    );
    return data;
};

/**
 * Fetch the benchmark auto-suggested for a given risk band + realised net
 * volatility. Pure, deterministic server-side rule mapping -- used to pre-select
 * the dropdown default and render the suggestion badge.
 */
export const fetchBenchmarkSuggestion = async (
    riskProfile: string,
    netVolatility: number,
): Promise<BenchmarkSuggestionResponse> => {
    const { data } = await apiClient.get<BenchmarkSuggestionResponse>(
        '/benchmarks/suggest',
        {
            params: {
                risk_profile: riskProfile,
                net_volatility: netVolatility,
            },
        },
    );
    return data;
};

/**
 * Fetch the heavy comparison math + date-aligned curves for a report against a
 * benchmark. The backend re-simulates the benchmark over the report's exact
 * window with the same capital / contribution cadence (gross of fees & taxes).
 */
export const compareStrategyToBenchmark = async (
    reportId: string,
    benchmarkId: string,
    startDate?: string,
    endDate?: string,
): Promise<BenchmarkCompareResponse> => {
    const { data } = await apiClient.get<BenchmarkCompareResponse>(
        `/benchmarks/compare/${reportId}`,
        {
            params: {
                benchmark_id: benchmarkId,
                start_date: startDate,
                end_date: endDate,
            },
        },
    );
    return data;
};

/**
 * Build the absolute URL for the holdings CSV export. Reuses the axios client's
 * baseURL (which already includes /api/v1) so the JWT-authenticated backend host
 * is honoured; collapses any accidental double slashes in the joined path.
 *
 * When an `investment` (notional capital) is supplied, it is forwarded as a
 * query parameter so the institutional-grade CSV can print real-dollar
 * "Target Value (USD)" allocations that match the UI's X-Ray table.
 */
export const getCsvDownloadUrl = (
    reportId: string,
    investment?: number,
): string => {
    const base =
        apiClient.defaults.baseURL || import.meta.env.VITE_API_URL || '';
    let url = `${base}/reports/${reportId}/download-csv`.replace(
        /([^:]\/)\/+/g,
        '$1',
    );
    if (investment !== undefined && Number.isFinite(investment)) {
        url += `?investment=${encodeURIComponent(investment)}`;
    }
    return url;
};

// ============================================================================
// ROBO-ADVISOR & TACTICAL MANAGEMENT API
// ============================================================================
//
// Powers the Institutional Command Center on the Strategy Tear Sheet. The
// Managed Portfolio router is mounted at /api/v1/managed; the shared apiClient
// already bakes in the /api/v1 prefix (and attaches the WordPress JWT), so we
// hit the bare `/managed/*` paths here. Mirrors backend/app/api/managed_portfolio.py:
//   POST /managed/{id}/enable                      -> enrol (409 if 15-cap hit)
//   POST /managed/{id}/disable                     -> withdraw from management
//   POST /managed/{id}/execute-tactical-rebalance  -> regenerate + execute orders
//   GET  /managed/{id}/dashboard                   -> live holdings + trades + alpha

/** A single generated Buy/Sell order row for the tactical trade ledger. */
export interface TradeInstructionRow {
    symbol: string;
    asset_class: string;
    action: 'BUY' | 'SELL' | 'HOLD';
    /** Change in portfolio allocation (target - current), as a fraction. */
    weight_delta: number;
    /** Estimated fee-adjusted dollar value of the trade. */
    net_trade_value: number;
}

/** Master payload for an actively managed (Robo-Advisor) portfolio dashboard. */
export interface AutoManagedDashboardResponse {
    report_id: string;
    is_auto_managed: boolean;
    current_regime: string;
    total_nav: number;
    current_holdings: GlobalPortfolioRow[];
    pending_trades: TradeInstructionRow[];
    monthly_excess_alpha: MonthlyExcessAlphaPoint[];
    /** UTC ISO timestamp of the most recent tactical rebalance (null if never). */
    last_rebalanced_at?: string | null;
    /** True when already rebalanced today — the manual "Force Rebalance" is locked out. */
    is_locked_for_today?: boolean;
}

/**
 * Enrol a strategy in Robo-Advisor auto-management. Throws a sentinel
 * `LIMIT_REACHED` error on the backend's HTTP 409 cap (max 15 live strategies)
 * so callers can surface a precise message.
 */
export const enableRoboAdvisor = async (id: string): Promise<void> => {
    try {
        await apiClient.post(`/managed/${id}/enable`);
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
            throw new Error('LIMIT_REACHED');
        }
        throw new Error('Failed to enable Robo-Advisor');
    }
};

/** Withdraw a strategy from Robo-Advisor auto-management. */
export const disableRoboAdvisor = async (id: string): Promise<void> => {
    try {
        await apiClient.post(`/managed/${id}/disable`);
    } catch {
        throw new Error('Failed to disable Robo-Advisor');
    }
};

/** Outcome of a tactical rebalance run. ``status`` distinguishes a real
 * execution (``executed`` / ``no_trades``) from a benign no-op
 * (``no_targets`` / ``skipped``) or a caught engine failure (``error``). */
export interface TacticalRebalanceResult {
    status:
        | 'executed'
        | 'no_trades'
        | 'no_targets'
        | 'skipped'
        | 'error'
        | 'not_found'
        | 'locked_or_not_found'
        | string;
    portfolio_id?: string;
    num_trades?: number;
    regime?: string;
    total_nav?: number;
    reason?: string;
}

/**
 * Force the AI to regenerate today's optimal target, diff it against the live
 * drifted book, and execute + persist the Buy/Sell ledger. Throws only on an
 * HTTP-level failure; a benign no-op is returned via ``status`` so the caller
 * can give precise feedback instead of a generic error.
 */
export const executeTacticalRebalance = async (
    id: string,
): Promise<TacticalRebalanceResult> => {
    try {
        const { data } = await apiClient.post<TacticalRebalanceResult>(
            `/managed/${id}/execute-tactical-rebalance`,
        );
        return data;
    } catch {
        throw new Error('Failed to execute Forge Ultra rebalance');
    }
};

/** Fetch the live Robo-Advisor dashboard (holdings + pending trades + alpha). */
export const fetchManagedDashboard = async (
    id: string,
): Promise<AutoManagedDashboardResponse> => {
    const { data } = await apiClient.get<AutoManagedDashboardResponse>(
        `/managed/${id}/dashboard`,
    );
    return data;
};
